import React, { useCallback } from 'react';

import { useRouter } from 'expo-router';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { useStoreActions } from 'features/store/pages/pickup-store-selector/hooks/use-store-actions';
import { Pressable, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { IRestaurant } from '@rbi-ctg/store';
import ActionButton, { ActionButtonSizes } from 'components/action-button';
import { Box, Typography } from 'components/ui';
import { t } from 'state/intl/translations';
import { getDesignToken, primitive, spacing } from 'styles/constants';

type SelectRestaurantFooterProps = {
  restaurant?: IRestaurant;
  onSelectStore?: (storeId: string) => void;
  isEligible?: boolean;
  currentRestaurant?: IRestaurant | null;
  canContinueWithCurrentRestaurant?: boolean;
};

export const SelectRestaurantFooter = ({
  restaurant,
  onSelectStore,
  isEligible = true,
  currentRestaurant,
  canContinueWithCurrentRestaurant = true,
}: SelectRestaurantFooterProps) => {
  const insets = useSafeAreaInsets();
  const { back, canGoBack, dismiss } = useRouter();

  const handleContinueWithCurrent = useCallback(() => {
    if (canGoBack()) {
      back();
      return;
    }
    dismiss();
  }, [back, canGoBack, dismiss]);

  return (
    <Box style={[styles.footer, { paddingBottom: Math.max(insets.bottom, spacing.$4) }]}>
      {restaurant ? (
        <EnabledSelectButton
          restaurant={restaurant}
          onSelectStore={onSelectStore}
          isEligible={isEligible}
        />
      ) : (
        <ActionButton
          testID="select-this-restaurant-button"
          fullWidth
          size={ActionButtonSizes.LARGE}
          disabled
        >
          {t('selectThisRestaurant')}
        </ActionButton>
      )}
      {currentRestaurant?._id && canContinueWithCurrentRestaurant ? (
        <Pressable
          accessibilityRole="button"
          onPress={handleContinueWithCurrent}
          style={styles.continueButton}
          testID="continue-with-current-restaurant"
        >
          <Typography variant="copyOne" color={primitive.$bbqBrown} style={styles.continueLabel}>
            {t('continueWithCurrentRestaurant', 'Continue with Current Restaurant')}
          </Typography>
        </Pressable>
      ) : null}
    </Box>
  );
};

const EnabledSelectButton = ({
  restaurant,
  onSelectStore,
  isEligible = true,
}: {
  restaurant: IRestaurant;
  onSelectStore?: (storeId: string) => void;
  isEligible?: boolean;
}) => {
  const { isStoreAvailableForPickup } = useStoreAvailabilityFns();
  const { onOrderPress } = useStoreActions({ restaurant });
  const canSelect = isStoreAvailableForPickup(restaurant) && isEligible;

  const handlePress = useCallback(() => {
    if (onSelectStore && restaurant._id) {
      onSelectStore(restaurant._id);
      return;
    }
    onOrderPress();
  }, [onOrderPress, onSelectStore, restaurant._id]);

  return (
    <ActionButton
      testID="select-this-restaurant-button"
      fullWidth
      size={ActionButtonSizes.LARGE}
      disabled={!canSelect}
      onPress={handlePress}
    >
      {t('selectThisRestaurant')}
    </ActionButton>
  );
};

const styles = StyleSheet.create({
  footer: {
    alignItems: 'center',
    backgroundColor: primitive.$white,
    borderColor: getDesignToken('border-color-options'),
    borderTopWidth: 1,
    gap: spacing.$3,
    paddingHorizontal: spacing.$4,
    paddingTop: spacing.$4,
    width: '100%',
  },
  continueButton: {
    paddingVertical: spacing.$2,
  },
  continueLabel: {
    fontWeight: '700',
    textAlign: 'center',
  },
});
